import { DateUtils, useIsMobile } from '@brunommsilva/react-foundations';
import {
  DateRangePicker,
  DebouncedTextField,
  Pagination,
} from '@brunommsilva/react-foundations';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccountsDropdown from '../components/accounts/accounts-dropdown';
import CategoriesDropdown from '../components/categories/categories-dropdown';
import PageFab from '../components/page-fab/page-fab';
import CreateTransactionForm from '../components/transactions/forms/create-transaction-form';
import TransactionTypeDropdown from '../components/transactions/transaction-type-dropdown';
import TransactionsList from '../components/transactions/transactions-list';
import TransactionsSummary from '../components/transactions/transactions-summary';
import {
  FilterFields,
  TransactionsFilter,
} from '../hooks/transactions/filter/filter';
import TransactionsFilterStorage from '../hooks/transactions/filter/storage';
import useTransactions from '../hooks/transactions/use-transactions';
import { TransactionType } from '../models/transactions';

const TransactionsPage = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [filter, setFilter] = useState<TransactionsFilter>(() =>
    TransactionsFilterStorage.get()
  );
  const [showFilters, setShowFilters] = useState(!isMobile);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);

  const updateFilter = (newFields: FilterFields) => {
    setFilter((prev) => prev.clone(newFields));
  };

  useEffect(() => {
    TransactionsFilterStorage.set(filter);
  }, [filter]);

  const clearAllFilters = () => {
    setFilter(new TransactionsFilter());
    setPage(1);
  };

  const handleCreateSuccess = useCallback(() => {
    setIsCreateDialogOpen(false);
  }, []);

  const handleCreateCancel = useCallback(() => {
    setIsCreateDialogOpen(false);
  }, []);

  const renderCreateTransactionForm = () => {
    return (
      <CreateTransactionForm
        onSuccess={handleCreateSuccess}
        onCancel={handleCreateCancel}
        isDialog={isMobile}
      />
    );
  };

  const apiFilter = {
    dateFrom: filter.dateFrom,
    dateTo: filter.dateTo,
    type: filter.type,
    categoryId: filter.category?.id,
    accountId: filter.account?.id,
    description: filter.description,
  };
  const { transactions, total, isLoading, hasError } = useTransactions(
    page,
    perPage,
    apiFilter
  );

  const changePage = (newPage: number) => {
    setPage(newPage);
  };

  const changePerPage = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const renderFilterBar = () => {
    return (
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'center',
          }}
        >
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <DateRangePicker
              size="small"
              labels={{
                from: t('TRANSACTION_DATE_FROM'),
                to: t('TRANSACTION_DATE_TO'),
              }}
              defaultDateRange={{
                dateFrom: DateUtils.fromString(filter.dateFrom),
                dateTo: DateUtils.fromString(filter.dateTo),
              }}
              onPicked={({ dateFrom, dateTo }) => {
                updateFilter({
                  dateFrom: DateUtils.toString(dateFrom),
                  dateTo: DateUtils.toString(dateTo),
                });
                setPage(1);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <TransactionTypeDropdown
              size="small"
              selected={
                filter.type
                  ? {
                      id: filter.type,
                      name: t(`TRANSACTION_TYPE_${filter.type}`),
                    }
                  : undefined
              }
              onSelected={(type) => {
                updateFilter({ type: type?.id as TransactionType });
                setPage(1);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <AccountsDropdown
              size="small"
              label={t('TRANSACTION_ACCOUNT')}
              selected={filter.account}
              onSelected={(account) => {
                updateFilter({ account });
                setPage(1);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <CategoriesDropdown
              size="small"
              label={t('TRANSACTION_CATEGORY')}
              selected={filter.category}
              onSelected={(category) => {
                updateFilter({ category });
                setPage(1);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 4,
            }}
          >
            <DebouncedTextField
              fullWidth
              size="small"
              label={t('TRANSACTION_DESCRIPTION')}
              value={filter.description || ''}
              onChange={(val) => {
                updateFilter({ description: val });
                setPage(1);
              }}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderTransactions = () => {
    return (
      <Card variant="outlined">
        <CardHeader
          title={
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {t('TRANSACTIONS')}
            </Typography>
          }
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                size="small"
                onClick={clearAllFilters}
                color="secondary"
                disabled={!filter.hasFilter()}
              >
                {t('CLEAR_FILTERS')}
              </Button>
              <IconButton
                size="small"
                onClick={() => setShowFilters(!showFilters)}
                color={showFilters ? 'primary' : 'default'}
              >
                <FilterListIcon />
              </IconButton>
            </Box>
          }
        />
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Collapse in={showFilters}>{renderFilterBar()}</Collapse>
          <Box sx={{ px: 2, pt: 2, pb: 2 }}>
            {/* What all of it adds up to says little, and asks the database for everything. */}
            {filter.hasFilter() && (
              <Box sx={{ mb: 1 }}>
                <TransactionsSummary filter={apiFilter} />
              </Box>
            )}
            <TransactionsList
              transactions={transactions}
              isLoading={isLoading}
              isError={hasError}
            />
          </Box>
        </CardContent>
        <CardActions>
          <Grid
            container
            sx={{
              justifyContent: 'flex-end',
            }}
          >
            <Pagination
              page={page}
              perPage={perPage}
              total={total}
              onPageChange={changePage}
              onPerPageChange={changePerPage}
            />
          </Grid>
        </CardActions>
      </Card>
    );
  };

  return (
    <>
      <Grid container spacing={3}>
        {!isMobile && (
          <Grid
            size={{
              xs: 12,
              md: 4,
              lg: 3,
            }}
          >
            {renderCreateTransactionForm()}
          </Grid>
        )}
        <Grid
          size={{
            xs: 12,
            md: isMobile ? 12 : 8,
            lg: isMobile ? 12 : 9,
          }}
        >
          {renderTransactions()}
        </Grid>
      </Grid>

      {isMobile && (
        <>
          <PageFab onClick={() => setIsCreateDialogOpen(true)} />
          <Dialog
            open={isCreateDialogOpen}
            onClose={() => setIsCreateDialogOpen(false)}
            fullWidth
            maxWidth="xs"
          >
            <DialogTitle>{t('REGISTER_NEW_TRANSACTION')}</DialogTitle>
            <DialogContent>{renderCreateTransactionForm()}</DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
};

export default TransactionsPage;
